import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { useAsync } from "react-async";
import StudentTimesheetEntry from "../../api/models/studentTimesheetEntry";
import WeekItem from "../../api/models/timesheetWeekItem";
import { getEsyStudents, getStudentsForEntry } from "../../api/services/timesheetService";
import { daysOfWeek, isEntryValid, removeDuplicates } from '../../common/utils';
import { validateEntryMinutesForDay } from "./shared";
import { TaskTypeLookup } from "../../common/constants";
import DayEntry from "./types/dayEntry";
import TimesheetTaskEntries from './types/timesheetTaskEntries';
import Step2Props from "./types/step2.child";
import TimesheetWeekTask from "../../api/models/TimesheetWeekTask";
import TimesheetWeekEntry from "../../api/models/TimesheetWeekEntry";

/**
 * Returns the students available based on the options selected.
 */
const loadStudents = async ({ disciplineId, schoolId, startDate, endDate, districtId, isEsy }: any) => {
    return isEsy ? getEsyStudents(disciplineId, districtId, startDate, true) : getStudentsForEntry(disciplineId, schoolId, startDate, endDate, true);
}

/**
 * Entry options for groups
 */
const EntryForGroup = ({
    entryState,
    setEntryState,
    editNote,
    saveEntry,
    setHasChanges,
    hasChanges,
    setIsSaved,
    isSaved,
    isGroupLoaded,
    setIsGroupLoaded,
    setEntryTasks,
    studentIds,
    setStudentIds,
    setShowSavedMessage,
    selectedDistrictProgramOption
}: Step2Props) => {
    //selection
    const [selectedStudents, setSelectedStudents] = useState<StudentTimesheetEntry[]>(entryState.selectedStudents ?? []);

    //options
    const [students, setStudents] = useState<StudentTimesheetEntry[]>(removeDuplicates(entryState.students ?? [], 'studentId'));
    const [noStudents, setNoStudents] = useState<boolean>(false);

    //validation
    const [validationMsg, setValidationMsg] = useState<string | null | JSX.Element>();
    const [timesheetEntryTasks, setTimesheetEntryTasks] = useState<TimesheetTaskEntries[]>(entryState.timesheetEntryTasks ?? []);

    /**
     * If the timesheet task entries is empty, create it
     */
    useEffect(() => {
        if (isGroupLoaded || timesheetEntryTasks.length > 0) return;
        //Generate temp entries
        var tempEntries: DayEntry[] = [];
        daysOfWeek(entryState.startDate).forEach((day) => {
            tempEntries.push({
                dateOfEntry: day,
                notes: "",
                minutes: 0,
                enabled: day.isSameOrAfter(entryState.startDate) && day.isSameOrBefore(entryState.endDate),
                timesheetEntryId: undefined
            });
        });

        //If the timesheet task entries is empty, we must add the group entry
        if (timesheetEntryTasks.length === 0) {
            //Create new task
            const newTaskEntries: TimesheetTaskEntries[] = [{
                taskTypeId: TaskTypeLookup.Group,
                startDate: entryState.startDate,
                endDate: entryState.endDate,
                schoolId: entryState.schoolId,
                entries: JSON.parse(JSON.stringify(tempEntries)),
                isMakeupTime: false
            }, {
                taskTypeId: TaskTypeLookup.Group,
                startDate: entryState.startDate,
                endDate: entryState.endDate,
                schoolId: entryState.schoolId,
                entries: JSON.parse(JSON.stringify(tempEntries)),
                isMakeupTime: true
            }];

            setTimesheetEntryTasks(newTaskEntries);
            setEntryTasks && setEntryTasks(newTaskEntries);
        }

        //If the entries are empty, we must fill with blank entries
        if (timesheetEntryTasks?.some(x => x.entries?.length === 0)) {
            //Set the entries
            setTimesheetEntryTasks(current => {
                current.filter(x => x.entries?.length === 0).forEach(x => {
                    x.entries = { ...tempEntries };
                });
                return current;
            });
        }

        setEntryTasks && setEntryTasks(timesheetEntryTasks);
        setIsGroupLoaded && setIsGroupLoaded(true);
    }, []);

    /**
     * When the parent component save function is finished, this effect will run
     */
    useEffect(() => {
        if (isSaved) {
            resetEntries();
            setIsSaved(false);
            setIsGroupLoaded && setIsGroupLoaded(false);
        }
    }, [isSaved]);

    /**
     * Saves pending changes if there are any.
     *
     * @return {Promise<void>} Returns a promise that resolves when the pending changes are saved.
     */
    const savePendingChanges = async () => {
        //if we have pending changes, save them.
        if (hasChanges) {
            const didSave = await saveEntry();
            if (didSave) {
                setHasChanges(false);
            }
        }
    }

    /**
     * Loads the students available based on the options selected
     */
    useAsync({
        promiseFn: loadStudents,
        disciplineId: entryState.disciplineId,
        startDate: entryState.startDate,
        endDate: entryState.endDate,
        schoolId: entryState.schoolId,
        districtId: entryState.district?.districtId,
        isEsy: entryState.isEsy,
        onResolve: (res) => {
            const s = removeDuplicates(res.data ?? [], 'studentId').sort((a, b) => a.studentName!.localeCompare(b.studentName!));
            setStudents(s ?? res.data);
            entryState.students = s ?? res.data;
            if ((s ?? res.data).length === 0)
                setNoStudents(true);
        }
    });

    /**
     * Adds a student to the group entry
     * @param studentId 
     * @returns 
     */
    const addStudent = async (studentId: number) => {
        //Save any pending changes
        //savePendingChanges();

        var student = students.find(s => s.studentId === studentId);
        const ss = [...selectedStudents];
        ss?.unshift(student!);
        setSelectedStudents([...ss]);
        //make sure the entrystate is the same
        entryState.selectedStudents = ss;

        //checks for existing entries and start/end dates
        //resetEntries();
        setEntryState(entryState);
        setStudentIds && setStudentIds([...(studentIds ?? []), studentId]);

        const sIds: number[] = [];
        ss.map(s => sIds.push(s.studentId));
        return ss;
    }

    /**
     * Removes a student from the group entry
     * @param studentId 
     * @returns 
     */
    const removeStudent = async (studentId: number) => {
        //Save any pending changes
        //savePendingChanges();

        var student = students.find(s => s.studentId === studentId);
        const ss = [...selectedStudents];
        var index = selectedStudents.indexOf(student!);
        ss.splice(index, 1);
        setSelectedStudents([...ss]);
        //make sure the entrystate is the same
        entryState.selectedStudents = ss;

        //checks for existing entries and start/end dates
        //resetEntries();
        setEntryState(entryState);
        setStudentIds && setStudentIds(current => current.filter(x => x !== studentId));

        const studentIds: number[] = [];
        ss.map(s => studentIds.push(s.studentId));
        return ss;
    }

    /**
     * Toggles a student in the group entry
     * @param studentId 
     */
    const addRemoveStudent = async (studentId: number) => {
        setShowSavedMessage!(false)
        if (selectedStudents.some(item => studentId === item.studentId)) {
            removeStudent(studentId);
        } else {
            addStudent(studentId);
        }

        setValidationMsg(null);
        entryState.hasValidationErrors = false;
        const tmpEntries = [...timesheetEntryTasks];
        tmpEntries.map(entryTask => {
            const entries = entryTask.entries;

            for (var i = 0; i < entries.length; i++) {
                if (entries[i].minutes !== undefined) {
                    var msg = validateEntryMinutesForDay(entryState, entries, entries[i].minutes!);
                    if (msg != null) {
                        setValidationMsg(msg);
                        break;
                    }
                }
            }
        });

        syncExistingEntries();
    }

    /**
     * Resets the entries.
     * @return {Promise<void>} Promise that resolves once the entries are reset.
     */
    const resetEntries = async () => {
        //Save any pending changes
        //savePendingChanges();
        var startDate = moment(entryState.startDate);
        var endDate = moment(entryState.startDate).add(7, "days");

        setSelectedStudents([]);
        setStudentIds && setStudentIds([]);

        var tempEntries: DayEntry[] = [];
        daysOfWeek(entryState.startDate).forEach((day) => {
            tempEntries.push({
                dateOfEntry: day,
                notes: "",
                minutes: 0,
                enabled: day.isSameOrAfter(startDate) && day.isSameOrBefore(endDate),
                timesheetEntryId: undefined
            });
        });

        // syncExistingEntries(tempEntries);
        setTimesheetEntryTasks(current => {
            current.map(entryTask => entryTask.entries = tempEntries);
            return current;
        });
        setEntryTasks && setEntryTasks(current => {
            current.map(entryTask => entryTask.entries = tempEntries);
            return current;
        })
        setEntryState(entryState);
        setHasChanges(false);
    }

    const compareNumberArrays = (array1: number[], array2: number[]) => {
        array1 = array1.sort();
        array2 = array2.sort();

        return JSON.stringify(array1) === JSON.stringify(array2);
    }

    /**
     * If the selected timesheet does not have the item in question, add the item
     */
    const addTimesheetItem = () => {
        //if (!studentId) return;
        //Check to see if the task exists in the timesheet
        const selectedStudentIds = selectedStudents.map(x => x.studentId);
        const itemStudents = entryState.selectedTimesheet?.items?.map(x => x.students ?? []);

        if (itemStudents?.some(x => compareNumberArrays(x, selectedStudentIds))) return;

        var tempEntries: TimesheetWeekEntry[] = [];
        daysOfWeek(entryState.startDate).forEach((day) => {
            tempEntries.push({
                dateOfEntry: day.toDate(),
                notes: "",
                minutes: 0,
                enabled: day.isSameOrAfter(entryState.startDate) && day.isSameOrBefore(entryState.endDate),
                entryId: TaskTypeLookup.Group,
                timesheetEntryFlags: []
            });
        });;

        //Create the task
        const tasks: TimesheetWeekTask[] = [{
            taskTypeName: 'Group',
            taskTypeId: TaskTypeLookup.Group,
            entryTypeName: entryState.entryTypeName ?? '',//entryTask.entryTypeName,
            entryTypeId: entryState.entryTypeId ?? 0,//entryTask.entryTypeId,
            programName: entryState.programName ?? '',//entryTask.programName,
            programId: entryState.programId,//entryTask.programId,
            schoolId: entryState.schoolId ?? 0,
            schoolName: entryState.school?.name ?? '',
            entries: tempEntries,
            isMakeupTime: true
        }, {
            taskTypeName: 'Group',
            taskTypeId: TaskTypeLookup.Group,
            entryTypeName: entryState.entryTypeName ?? '',//entryTask.entryTypeName,
            entryTypeId: entryState.entryTypeId ?? 0,//entryTask.entryTypeId,
            programName: entryState.programName ?? '',//entryTask.programName,
            programId: entryState.programId,//entryTask.programId,
            schoolId: entryState.schoolId ?? 0,
            schoolName: entryState.school?.name ?? '',
            entries: tempEntries,
            isMakeupTime: false
        }];
        let title = '';
        selectedStudents.map(student => title += `${student.studentName};`);

        //Create the item
        const tmpItem: WeekItem = {
            title: title,
            subTitle: entryState.schoolName ?? '',
            description: selectedStudents.flatMap(x => x.studentName).join('; '),
            districtId: entryState.districtId ?? 0,
            schoolId: entryState.schoolId ?? 0,
            students: selectedStudentIds,
            billToId: entryState.billToId,
            tasks: tasks,
            day: entryState.startDate,
        };

        //Make sure the items array is actually an array
        if (entryState.selectedTimesheet && !entryState.selectedTimesheet.items) {
            entryState.selectedTimesheet.items = [];
        }

        //Add the temp item
        entryState.selectedTimesheet?.items?.push(tmpItem);
        setEntryState(entryState);
    }

    /**
     * Check for existing entries that matches the options selected, no duplicated entries! 
     * @param tempEntries 
     * @returns 
     */
    const syncExistingEntries = () => {
        setTimesheetEntryTasks(current => {
            current.map(entryTask => {
                //clear old values
                entryTask.entries.map((entry) => {
                    entry.notes = "";
                    entry.minutes = undefined;
                    return entry;
                });


                //make sure we have a selected timesheet
                if (typeof entryState.selectedTimesheet === "undefined")
                    return;

                //look for matching entries and update the temp entries
                var item: WeekItem | undefined;

                entryTask.entries.map(entry => {
                    //grab a comma separated list of selected student Ids
                    let selectedIds = entryState.selectedStudents
                        ?.map(student => student.studentId)
                        ?.sort((a, b) => (a > b) ? 1 : -1);
                    let a = selectedIds?.toString();

                    //go through existing entries and try to find a group entries that matches the current selection 
                    entryState.selectedTimesheet?.items?.forEach(weekItem => {
                        if (typeof weekItem.students === "undefined")
                            return;

                        weekItem.students?.sort((a: number, b: number) => (a > b) ? 1 : -1);

                        var b = weekItem.students?.toString();
                        if (a === b) {
                            item = weekItem;
                        }
                    });

                    //find a task that matches the selection
                    var tasks = item?.tasks?.filter(t =>
                        t.entryTypeId === entryState.entryTypeId &&
                        (t.taskTypeId === -1 || t.taskTypeId === TaskTypeLookup.Group) &&
                        t.schoolId === entryState.schoolId &&
                        t.isMakeupTime === entryTask.isMakeupTime);

                    if (entryState.programId && entryState.programId > 0)
                        tasks = tasks?.filter(x => x.programId === entryState.programId);

                    var task = tasks ? tasks[0] : undefined;

                    if (typeof task !== 'undefined') {
                        var date = moment(entry.dateOfEntry ?? entry.date);
                        var tempEntry = task.entries.find(e => date.isSame(e.dateOfEntry ?? e.date));
                        if (tempEntry) {
                            entry.notes = tempEntry.notes.valueOf();
                            entry.minutes = tempEntry.minutes;
                            entry.entryId = tempEntry.entryId;
                            entry.timesheetEntryId = tempEntry.entryId.valueOf();
                        }
                        else {
                            entry.timesheetEntryId = undefined;
                            entry.entryId = undefined;
                        }
                    }

                    return entry;
                });
            });
            return current;
        });
    }

    /**
     * When the user changes the mins value for one of the days
     * @param index 
     * @param inputMinutes 
     */
    const updateEntryTime = (index: number, inputMinutes: number, isMakeupTime: boolean) => {
        const isValid = isEntryValid(timesheetEntryTasks, index, inputMinutes);
        let entry = timesheetEntryTasks.find(x => x.isMakeupTime === isMakeupTime);
        if (entry) {
            //if there is a validation error users can't add more time to other entries, but they are allowed to reduce, otherwise they can't change anything
            if (entryState.hasValidationErrors && !isValid) {
                alert("You have validation errors, please fix those issues before adjusting other entries");
                return;
            }

            //check's validation, set's the validation message to the result of the validation


            addTimesheetItem();

            // Update the entries
            setTimesheetEntryTasks(current => {
                const newEntries = [...current];
                let tmp = newEntries.find(x => x.isMakeupTime === isMakeupTime);
                if (tmp) {
                    if (tmp.entries.length === 0) {
                        var tempEntries: DayEntry[] = [];
                        daysOfWeek(entryState.startDate).forEach((day, i) => {
                            tempEntries.push({
                                dateOfEntry: day,
                                notes: "",
                                minutes: i === index ? inputMinutes : undefined,
                                enabled: day.isSameOrAfter(entryState.startDate) && day.isSameOrBefore(entryState.endDate),
                                makeupTime: isMakeupTime
                            });
                        });
                        tmp.entries = tempEntries;
                    }
                    tmp.entries[index].minutes = inputMinutes;
                }

                entryState.timesheetEntryTasks = newEntries;
                setEntryState(entryState);
                setValidationMsg(
                    validateEntryMinutesForDay(entryState, entry.entries, inputMinutes)
                );
                return newEntries;
            });

            // entryState.timesheetEntryTasks = timesheetEntryTasks;
            setEntryTasks && setEntryTasks(timesheetEntryTasks);
            // syncExistingEntries();

            // Update entries on timesheet
            const entryTask = timesheetEntryTasks.find(x => x.isMakeupTime === isMakeupTime);
            if (!entryTask) {
                return;
            }


            entryState.selectedTimesheet?.items
                ?.filter(x => x.students === selectedStudents.flatMap(x => x.studentId))
                ?.map(item => {
                    if (!(item?.tasks?.some(x => x.taskTypeId === entryTask.taskTypeId))) {
                        return item;
                    }

                    item?.tasks?.map(task => {
                        if (task.taskTypeId === entryTask.taskTypeId/* && task.entries.some(x => moment(x.dateOfEntry ?? x.date).isBetween(entryTask.startDate, entryTask.endDate))*/) {
                            task.entries = entryTask.entries;
                            return task;
                        }
                        return task;
                    })
                    return item;
                });

            setEntryState(entryState);
            setEntryState(entryState => {
                const tmpEntryState = { ...entryState };
                tmpEntryState.timesheetEntryTasks = timesheetEntryTasks;
                return tmpEntryState;
            });
            setHasChanges(true);
        }

    }

    /**
     * Displays the notes when the edit link is clicked
     * @param index number
     * @param taskTypeId number
     */
    const editNoteClicked = (index: number, isMakeupTime: boolean) => {
        //Set the entry state
        setEntryState(entryState => {
            const tmpEntryState = { ...entryState };
            tmpEntryState.timesheetEntryTasks = timesheetEntryTasks;
            return tmpEntryState;
        });
        //Set our task type
        const taskTypeId = TaskTypeLookup.Group;

        //Get entries
        const searchEntries = (timesheetEntryTasks.find(x => x.taskTypeId === taskTypeId && x.isMakeupTime === isMakeupTime)?.entries as unknown) as DayEntry[] ?? [];
        //Reset the state of the validtion errors, they will be checked again with this component loads
        entryState.hasValidationErrors = false;
        setValidationMsg(null);

        //Load the edit note dialog
        editNote({
            dayEntry: searchEntries![index],
            entryModel: entryState,
            isMakeupTime: isMakeupTime,
            taskTypeId
        });
        setHasChanges(true);
    }

    /**
     * This will revalidate each entry after the user edit notes
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setValidationMsg(null);
        timesheetEntryTasks.map(entryTask => {
            const entries = entryTask.entries;

            for (var i = 0; i < entries.length; i++) {
                if (entries[i].minutes !== undefined) {
                    var msg = validateEntryMinutesForDay(entryState, entries, entries[i].minutes!);
                    if (msg != null) {
                        setValidationMsg(msg);
                        break;
                    }
                }
            }
        });
    }, []);

    return (
        <Fragment>
            {/* We need students to show the options, display message if none exist. */}
            {noStudents && (
                <div className="text-sm text-red-900">The selected school doesn't have any students active for this week, please select another school</div>
            )}

            {/* If we have students display the options */}
            {!noStudents && students.length > 0 && (
                <Fragment>
                    <label>Select Students</label>
                    <div className="overflow-y-auto max-h-48">
                        {students.map((student, i) => {
                            const etid = `entrytype_${i}`;

                            return (
                                <div key={etid} className={`flex items-start py-1 ${student.studentId}-${student.startDate}-${student.endDate}`}>
                                    <div className='flex items-center h-5 ml-1'>
                                        <input
                                            id={etid}
                                            value={student.studentId}
                                            checked={selectedStudents.filter(s => s.studentId === student.studentId).length > 0}
                                            onChange={(e) => addRemoveStudent(Number(e.target.value))}
                                            name='entry-type'
                                            type='checkbox'
                                        />
                                    </div>
                                    <div className='ml-3 text-sm'>
                                        <label htmlFor={etid}>{student.studentName}{/*} ({moment(student.startDate).format("MMMM D")} - {student.endDate !== null ? moment(student.endDate).format("MMMM D") : "none"})*/}</label>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Fragment>
            )}

            {timesheetEntryTasks && timesheetEntryTasks[0] && timesheetEntryTasks[0]?.entries && timesheetEntryTasks[0]?.entries?.length > 0 && selectedStudents.length > 0 && (
                <div className="mt-3 text-sm italic">Note: some entries are disabled because a selected student can't be billed on that day.</div>
            )}

            {/* If a student and tasktype is selected, display the week entries */}
            {(timesheetEntryTasks?.length ?? 0) > 0 && selectedStudents.length > 0 &&
                <div className={`entries-container overflow-x-hidden drop-shadow-lg ${timesheetEntryTasks.length > 0 ? 'max-h-96 block' : 'hidden'} ${timesheetEntryTasks.length >= 4 ? 'overflow-scroll scrollbar' : 'overflow-none'} mt-8`}>
                    {timesheetEntryTasks?.map((et, index) => {
                        return (
                            <div className="entryTaskContainer" style={{ backgroundColor: et?.isMakeupTime ? '#FFC874' : '' }}>
                                <h3 className="block ml-1">
                                    {et?.isMakeupTime ? "Group Makeup Time Only" : "Group Time"}
                                </h3>
                                <div className="flex flex-row w-full mt-2 justify-evenly">
                                    {et.entries?.map((entry, i) => {
                                        const dateOfEntry = moment(entry.dateOfEntry ?? entry.date);
                                        const payrateExists = selectedDistrictProgramOption?.payrates!.some(pr => {
                                            if (!pr.payrateStartDate) return false;
                                            const start = moment(pr.payrateStartDate);
                                            const end = pr.payrateEndDate ? moment(pr.payrateEndDate) : moment();
                                            if (!start.isValid() || !end.isValid()) return false;
                                            return dateOfEntry.isBetween(start, end, undefined, '[]');
                                        }) ?? false;
                                        const isEntryEnabled = (entry.enabled && dateOfEntry >= entryState.startDate && dateOfEntry <= entryState.endDate && payrateExists && !selectedStudents.some(x => moment(x.endDate).isSameOrBefore(dateOfEntry))) && !moment().isBefore(dateOfEntry);
                                        return (

                                            <div className="w-20 text-center" key={`${index}_${i}`}>
                                                {/** Date of entry */}
                                                <div className={`font-bold ${!isEntryEnabled ? "text-gray-300" : ""}`}>
                                                    {dateOfEntry.format('ddd')}
                                                </div>
                                                <div className={`text-xs mb-2 ${!isEntryEnabled ? "text-gray-300" : ""}`}>
                                                    {dateOfEntry.format('M/D')}
                                                </div>

                                                {/** Minutes Text Box */}
                                                <input className={`inline-block text-center rounded-md h-12 w-12 appearance-none border mb-1 ${isEntryEnabled ? "border-black" : "border-slate-100 bg-gray-100"}`}
                                                    value={entry.minutes ? entry.minutes.valueOf() : "" ?? ""}
                                                    onChange={(e) => updateEntryTime(i, Number(e.target.value.replace(/\D/g, "")), et.isMakeupTime!)}
                                                    disabled={!isEntryEnabled} />

                                                {/** Add/Edit Notes */}
                                                {(entry.minutes ?? 0) > 0 && (
                                                    <div className="cursor-pointer text-ptsblue-dark" onClick={(e) => { editNoteClicked(i, et.isMakeupTime!); }}>{(entry.notes.length === 0) ? "Add Note" : "Edit Note"}</div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <br />
                            </div>
                        )
                    })
                    }
                </div >
            }

            {/** Validation Message */}
            {
                validationMsg != null && typeof validationMsg === "string" && (
                    <div className="text-sm text-red-900">{validationMsg}</div>
                )
            }

            {validationMsg != null && typeof validationMsg !== "string" && validationMsg}
        </Fragment >
    );
}

export default EntryForGroup;